// Canonical error model. Every bino Cloud API failure has the body
// {"code":"...","message":"..."}. This module normalizes anything thrown by the
// fetch wrapper or the PocketBase SDK into {code, message, status} and maps a
// code to a human-readable message. Pure (no DOM/SDK dependency in the core map).

export interface ApiError {
  code: string;
  message: string;
  status?: number;
  /** Present when native OTP/password auth answered 401 {mfaId}. */
  mfaId?: string;
  /** The error body's `details` array, when present. */
  details?: unknown[];
  /** The teams a `sole_owner` refusal names: the shared teams the caller alone
   * owns and, with `personal` set, the caller's personal team. All of them have
   * other members. */
  teams?: { id: string; name: string; personal: boolean }[];
}

// code -> human message. Unknown codes fall back to the server-supplied message.
const MESSAGES: Record<string, string> = {
  validation_failed: "Some of the values are invalid. Check the form and try again.",
  name_taken: "That name is already taken. Choose a different one.",
  forbidden: "Your role does not allow this.",
  unauthorized: "Sign in to continue.",
  rate_limited: "Too many requests. Wait a while and try again.",
  team_not_found: "That team does not exist, or you are not a member of it.",
  user_not_found: "No account matches that user or email.",
  already_member: "That user is already a member of the team.",
  invariant_violation: "That change is not allowed.",
  workspace_not_found: "That workspace does not exist.",
  build_not_found: "That build does not exist.",
  artifact_not_found: "That file is no longer stored. Build files are removed after a while.",
  file_not_found: "That file does not exist.",
  secret_not_found: "That secret does not exist.",
  tag_not_found: "That tag no longer exists. It may have been deleted by a teammate.",
  tag_name_taken: "This team already has a tag with that name.",
  unknown_tag: "One of those tags is not available on this workspace's team.",
  too_many_tags: "Too many tags. A workspace can have at most 64, and a filter at most 20.",
  workspace_git_locked:
    "Files come from the connected git repository. Change them there, or disconnect it to edit them here.",
  workspace_in_repository:
    "This workspace belongs to a repository, and repositories are switched off. Its files cannot be changed until they are switched on again.",
  commit_conflict: "Someone changed these files since you opened them. Reload and try again.",
  repository_busy: "The repository is busy with another change. Nothing was saved. Try again in a moment.",
  workspace_not_hosted: "This workspace does not belong to a repository hosted here.",
  repository_over_quota:
    "The repository is over its size limit, or this change would put it over. While it is over, only changes that do not make the default branch larger are accepted.",
  storage_quota_exceeded:
    "Your team has used up its storage. Delete a repository or ask for a larger quota.",
  repository_empty: "This repository has no commits yet, so there is nothing to export.",
  export_busy: "Too many exports are running. Wait a moment and try again.",
  repository_not_found: "That repository does not exist.",
  build_queue_full: "Too many builds are waiting. Wait for some of them to finish.",
  nothing_committed: "This branch has no commit yet, so there is nothing to build. Commit or push first.",
  // Data area: uploads, seals and the S3 source.
  upload_not_found: "That upload no longer exists. Start it again.",
  upload_offset_mismatch: "The upload got out of step with the server. Pick the file again to resume.",
  upload_busy: "Another request is sending this file. Wait a moment and try again.",
  chunk_too_large: "A part of the upload was too large for the server.",
  too_many_uploads: "This repository has 16 unfinished uploads, which is the limit. Finish or delete some first.",
  path_conflict:
    "That path clashes with the data drop: a file is in its way, a folder has that name, or an upload of it with another size is pending. Delete that upload first.",
  drop_full: "This upload would put the data drop over its limit of 10,000 files or 50 GiB.",
  data_quota_exceeded:
    "This would take your team over its storage quota, or the repository over its limit of 200 GiB or 100,000 files of data. Delete data, or ask for a larger team quota.",
  no_open_drop: "This month has no open data drop to seal. Upload files first.",
  uploads_pending: "Some uploads of this month are not finished. Finish or delete them before you seal.",
  drop_empty: "The data drop for this month has no files, so there is nothing to seal.",
  data_source_not_found: "This repository has no S3 source.",
  invalid_endpoint:
    "The S3 endpoint or bucket name is not allowed. The endpoint must be a public HTTPS URL, and the bucket name 3-63 lower-case letters, digits and hyphens.",
  pull_running: "A pull for this repository is already running.",
  pull_busy: "Too many pulls are running. Wait a moment and try again.",
  // One message for both: PocketBase answers a wrong code and an expired one
  // with the identical codeless 400, on purpose, so the UI cannot honestly
  // tell them apart. See verifyOtp in api/auth.ts.
  otp_invalid: "That code is not valid or has expired. Check your email, or request a new code.",
  invalid_credential: "That passkey could not be verified. Try again, or sign in with an email code.",
  passkey_not_found: "That passkey does not exist.",
  registration_failed: "That passkey could not be added. Try again.",
  too_many_passkeys: "You can have at most 10 passkeys. Remove one to add another.",
  invalid_ceremony: "That passkey attempt expired. Try again.",
  duplicate_credential: "This passkey is already registered.",
  // Step-up verification (API keys, account export and deletion, team
  // deletion). Grants are single-use with a short TTL, so "invalid" almost
  // always means expired or already spent.
  verification_required: "Confirm it's you with a passkey or an email code.",
  verification_invalid: "That verification has expired or was already used. Verify again.",
  no_passkeys: "No passkeys are registered on this account. Verify with an email code instead.",
  personal_team: "A personal team cannot be deleted. It is removed with your account.",
  // The account page lists the teams from the error body next to this.
  sole_owner:
    "A team you alone own, or your personal team, still has other members. Make another member an owner, or remove the other members, first.",
  sso_popup_blocked: "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.",
  invalid_token: "That invitation link is not valid. Ask for a new one.",
  invite_expired: "That invitation has expired. Ask a team maintainer to send a new one.",
  email_mismatch: "That invitation was sent to a different email address.",
  // Shared by invitations and step-up OTP requests — keep the copy generic.
  email_send_failed: "The email could not be sent. Try again.",
  internal: "Something went wrong on the server. Try again.",
  internal_error: "Something went wrong on the server. Try again.",
  // Capacity and restarts: the cause is the server, never the input.
  no_capacity: "The server is busy. Try again in a minute.",
  capacity: "The server is busy. Try again in a minute.",
  concurrency_limit: "Too many builds and previews are running. Try again in a minute.",
  sandbox_interrupted: "The server restarted while this ran. Try again.",
  shutting_down: "The server is restarting. Try again in a minute.",
  bino_unsupported: "This server runs a bino version that is no longer supported. Ask your admin to update it.",
};

/**
 * errorMessage maps a canonical error code to a human-readable message. Known
 * codes return a curated message; an unknown code falls back to the server's own
 * message, and finally to a generic message.
 */
export function errorMessage(code: string, serverMessage?: string): string {
  const known = MESSAGES[code];
  if (known) return known;
  if (serverMessage && serverMessage.trim() !== "") return serverMessage;
  return "Something went wrong. Try again.";
}

/**
 * parseError normalizes anything thrown by fetch or the PocketBase SDK into the
 * canonical {code, message, status}. The SDK's ClientResponseError carries the
 * parsed JSON body on `.response`/`.data` and the HTTP status on `.status`; the
 * fetch wrapper throws an object already in this shape.
 */
export function parseError(e: unknown): ApiError {
  if (e && typeof e === "object") {
    const any = e as Record<string, unknown>;
    const status = typeof any.status === "number" ? (any.status as number) : undefined;
    const body = (any.response ?? any.data) as Record<string, unknown> | undefined;
    if (body && typeof body === "object") {
      const code = typeof body.code === "string" ? (body.code as string) : "";
      const message = typeof body.message === "string" ? (body.message as string) : "";
      const mfaId = typeof body.mfaId === "string" ? (body.mfaId as string) : "";
      if (mfaId) return { code: code || "mfa_required", message, status, mfaId };
      if (code || message) {
        const details = Array.isArray(body.details) ? (body.details as unknown[]) : undefined;
        return {
          code: code || codeForStatus(status),
          message,
          status,
          ...(details ? { details } : {}),
          ...teamsOf(body),
        };
      }
    }
    if (typeof any.code === "string" || typeof any.message === "string") {
      return {
        code: (any.code as string) || codeForStatus(status),
        message: (any.message as string) || "",
        status,
        ...teamsOf(any),
      };
    }
    if (status) return { code: codeForStatus(status), message: "", status };
  }
  return { code: "internal", message: e instanceof Error ? e.message : String(e ?? "") };
}

/** teamsOf picks the `teams` list from an error body, or from an ApiError the
 * fetch wrapper already parsed, so parsing that error again keeps it. */
function teamsOf(v: Record<string, unknown>): Pick<ApiError, "teams"> {
  return Array.isArray(v.teams) ? { teams: v.teams as NonNullable<ApiError["teams"]> } : {};
}

/** humanError is the convenience composition of parseError + errorMessage. */
export function humanError(e: unknown): string {
  const { code, message } = parseError(e);
  return errorMessage(code, message);
}

/**
 * detailedError is humanError for a form whose 400 answer says what is wrong
 * with the input, such as a secret name the builds reserve or a data path
 * with a bad segment: it shows the server's own message for a 400 that has
 * one, where the curated validation_failed copy would only say "invalid".
 */
export function detailedError(e: unknown): string {
  const { code, message, status } = parseError(e);
  if (status === 400 && message.trim() !== "") return message;
  return errorMessage(code, message);
}

function codeForStatus(status?: number): string {
  switch (status) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "workspace_not_found";
    case 409:
      return "name_taken";
    case 422:
      return "validation_failed";
    case 429:
      return "rate_limited";
    default:
      return "internal";
  }
}
